import { Controller } from "@hotwired/stimulus"
import { Flyout } from "../shared/flyout"

// Standalone flyout: a trigger button that reveals a floating panel beside or
// below it. Opens on click, dismisses on outside click, ancestor scroll, and
// Escape. Set `detach` to move the panel into a positioning root (the
// controller element by default) and position it with JS.
//
// The controller is also the public composition surface for the flyout: an
// outlet owner (e.g. the sidebar) can call configure()/open()/close()/toggle()
// to reuse the same engine with its own class names and dynamic placement, and
// listen for the dispatched `open`/`close` events to react to state changes.
export default class extends Controller {
  static targets = ["trigger", "panel"]
  static values = {
    placement: { type: String, default: "bottom-start" },
    detach: { type: Boolean, default: false },
    rootSelector: { type: String, default: "" },
    containerSelector: { type: String, default: "" },
    openClass: { type: String, default: "is-open" },
    detachedClass: { type: String, default: "aqui-flyout--detached" },
    closeOnEscape: { type: Boolean, default: true },
    closeOnScroll: { type: Boolean, default: true },
  }

  connect() {
    this.overrides = {}
    this.ensureFlyout()
  }

  disconnect() {
    this.flyout?.destroy()
    this.flyout = null
  }

  // --- Public API (also used by outlet owners) ------------------------------
  toggle(event) {
    event?.preventDefault()
    this.ensureFlyout().toggle()
  }

  open(event) {
    event?.preventDefault()
    this.ensureFlyout().open()
  }

  close() {
    this.flyout?.close()
  }

  // Update the flyout's runtime options. Applied in place so an already-open
  // flyout keeps its state; a fresh flyout is built if one does not exist yet.
  configure(options = {}) {
    Object.assign(this.overrides, options)

    if (this.flyout) {
      this.applyOverrides()
    } else {
      this.ensureFlyout()
    }
  }

  // --- Internals ------------------------------------------------------------
  ensureFlyout() {
    if (!this.flyout) {
      this.flyout = new Flyout(this.flyoutOptions())
    }

    return this.flyout
  }

  applyOverrides() {
    Object.entries(this.overrides).forEach(([key, value]) => {
      if (value !== undefined) this.flyout[key] = value
    })
  }

  flyoutOptions() {
    return {
      trigger: this.triggerTarget,
      panel: this.panelTarget,
      container: this.resolveContainer(),
      root: this.resolveRoot(),
      placement: this.placementValue,
      detach: this.detachValue,
      openClass: this.openClassValue,
      detachedClass: this.detachedClassValue,
      closeOnEscape: this.closeOnEscapeValue,
      closeOnScroll: this.closeOnScrollValue,
      onOpen: () => this.dispatch("open", { detail: { trigger: this.triggerTarget } }),
      onClose: () => this.dispatch("close", { detail: { trigger: this.triggerTarget } }),
      ...this.overrides,
    }
  }

  resolveRoot() {
    if (this.rootSelectorValue) {
      return document.querySelector(this.rootSelectorValue) || this.element
    }

    return this.element
  }

  resolveContainer() {
    if (this.containerSelectorValue) {
      return document.querySelector(this.containerSelectorValue) || this.element
    }

    return this.element
  }
}
